import api from "api";
import { UUID } from "crypto";
import { useMutation, useQuery, QueryConfig } from "react-query";

export interface IEnrichDataCompanyNews {
  date: string;
  link?: string;
  title: string;
  source?: string;
  snippet?: string;
  position?: number;
}
export interface IContactPhone {
  value: string;
  type: string;
}
export interface TagItem {
  id: number;
  name: string;
  color: string;
  num_contacts?: number;
}
export const EnrichStatus = {
  NOT_STARTED: "NOT_STARTED",
  IN_PROGRESS: "IN_PROGRESS",
  COMPLETED: "COMPLETED",
  FAILED: "FAILED",
} as const;

export type EnrichStatusType = (typeof EnrichStatus)[keyof typeof EnrichStatus];

export interface EnrichLeadCaptureContact {
  enrich_data?: IEnrichData;
  enrich_status?: EnrichStatusType;
  enrich_notification?: boolean;
  enriched_at?: string;
}

export interface IEnrichDataContactInfo {
  linkedin_headline?: string;
  linkedin_title?: string;
  country?: string;
  photo_url?: string;
  city?: string;
  state?: string;
  enriched_phone_number?: string;
  linkedin_url: string;
}
export interface IEnrichDataCompanyInfo {
  name?: string;
  company_logo?: string;
  company_country?: string;
  company_city?: string;
  company_state?: string;
  industry?: string;
  business_type?: string;
  head_count?: string;
  employee_name?: string;
  tenure?: string;
  linkedin_company_url?: string;
  company_website_url?: string;
  company_employee_growth?: string;
  website_title?: string;
  website_summary?: string;
  website_screenshot_url?: string;
}
export interface IEnrichDataCompanyNews {
  date: string;
  link?: string;
  title: string;
  source?: string;
  snippet?: string;
  position?: number;
}
export interface IEnrichData {
  info_found?: boolean;
  contact_info?: IEnrichDataContactInfo;
  company_info?: IEnrichDataCompanyInfo;
  company_news?: IEnrichDataCompanyNews[];
}

export interface LeadCapture {
  readonly id?: number | string;
  readonly uuid?: UUID;
  /** max length 200 */
  full_name: string;
  email?: string;
  emails: string[];
  /** max length 15 */
  phones?: IContactPhone[];
  job_title?: string;
  workplace?: string;
  message?: string;
  performed?: string;
  tags?: TagItem[];
  private_note?: string;
  type?: "connections" | "lead_captures";
  handle?: string;
  created?: string;
  contact_linkedin_url?: string;
  qr_link?: string;
  company_industry?: string;
  company_website?: string[];
  contact_address?: string[];
  company_name?: string;
  company_logo?: string;
  company_description?: string;
  company_country?: string;
  company_head_count?: string;
  company_city?: string;
  company_state?: string;
  company_linkedin_url?: string;
  company_employee_growth?: string;
  company_employee_name?: string;
  company_tenure?: string;
  company_business_type?: string;
  company_news?: IEnrichDataCompanyNews[];
  contact_linkedin_headline?: string;
  contact_linkedin_title?: string;
  contact_photo_url?: string;
  contact_enriched_phone_number?: string;
  company_website_screenshot_url?: string;
  company_website_summary?: string;
  company_website_title?: string;
  source_type?: string;
  scanned_image_url?: string;
  enrich_data?: IEnrichData;
  enrich_status?: EnrichStatusType;
  enrich_notification?: boolean;
  enriched_at?: string;
}
export const getConnectionById = async (
  contactId: string,
  options?: Record<string, any>,
): Promise<LeadCapture> => {
  const response = await api.get(
    `/contacts/lead_capture/${contactId}`,
    options,
  );
  return response.data;
};

export const getConnectionByIdQueryKey = (contactId: string) => [
  `/contacts/lead_capture/${contactId}`,
];

export const useGetConnectionById = (
  connectionId: string,
  options?: {
    query?: QueryConfig<LeadCapture, Error>;
    request?: Record<string, any>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options || {};

  const queryKey = getConnectionByIdQueryKey(connectionId);

  return useQuery<LeadCapture, Error>(
    queryKey,
    () => getConnectionById(connectionId, requestOptions),
    queryOptions,
  );
};

export const enrichLeadContact = async (connectionId: string) => {
  const response = await api.post(
    `/lead_capture/contact/${connectionId}/enrich`,
    {
      headers: { "Content-Type": "application/json" },
    },
  );
  return response.data;
};

export const useEnrichLeadContact = (options?: {
  onSuccess?: (data: any) => void;
  onError?: (error: any) => void;
}) => {
  const [mutate, { status, error, isLoading }] = useMutation(
    enrichLeadContact,
    {
      ...options,
    },
  );

  return {
    mutate,
    status,
    error,
    isLoading,
  };
};
